// Client-side LIVE PREVIEW of the rules engine, so the garage stat bars react
// as the user "customizes" their damage. Reads the same versioned dataset the
// server uses — but this is presentation only: the real estimate is always
// computed server-side (functions/estimate/engine.ts) at the end.
import rules from "../data/collision-rules.json";
import ops from "../data/labor-ops.json";

export interface DamagePreview {
  partCount: number;
  maxSeverity: number; // 0-10 (0 = nothing selected yet)
  hiddenProb: number; // 0-1
}

type PartsMap = Record<string, number>;

// Mirrors COLLISION_OWNS in functions/estimate/engine.ts.
const COLLISION_OWNS: Record<string, string[]> = {
  "front-impact": ["front"],
  "rear-impact": ["rear"],
  "side-impact-driver": ["driver-side"],
  "side-impact-passenger": ["passenger-side"],
  sideswipe: ["driver-side", "passenger-side"],
  rollover: ["roof"],
  "multi-point": ["front", "rear", "driver-side", "passenger-side"],
};

// Mirrors IMPACT_TO_COLLISION in functions/estimate/engine.ts.
const IMPACT_TO_COLLISION: Record<string, string> = {
  front: "front-impact",
  rear: "rear-impact",
  "driver-side": "side-impact-driver",
  "passenger-side": "side-impact-passenger",
};

export function previewStats(
  collisionType: string,
  impactPoints: string[],
  symptoms: string[],
): DamagePreview {
  const parts = new Map<string, number>();
  const merge = (m: PartsMap) => {
    for (const [k, v] of Object.entries(m)) {
      // Mirror the server engine's guard: only priced parts count.
      if (!(k in ops.parts)) continue;
      parts.set(k, Math.max(parts.get(k) ?? 0, v));
    }
  };

  const byCollision = rules.byCollision as Record<
    string,
    { parts: PartsMap; hiddenDamagePrior: number }
  >;
  const bc = collisionType ? byCollision[collisionType] : undefined;
  let prob = bc ? bc.hiddenDamagePrior : collisionType ? rules.hiddenDamageDefaultPrior : 0;
  // Mirror the engine's step 1b: a derived multi-point composes the selected
  // ends' own rules rather than the generic front+rear guess.
  const endRules =
    collisionType === "multi-point"
      ? impactPoints.map((p) => byCollision[IMPACT_TO_COLLISION[p] ?? ""]).filter(Boolean)
      : [];
  for (const rule of endRules.length ? endRules : bc ? [bc] : []) merge(rule.parts);

  // Mirror the engine's step 2: skip points the collision type already owns.
  const byImpact = rules.byImpact as Record<string, PartsMap>;
  const owned = COLLISION_OWNS[collisionType] ?? [];
  for (const p of impactPoints) if (!owned.includes(p) && byImpact[p]) merge(byImpact[p]);

  const mods = rules.symptomModifiers as Record<
    string,
    { addParts?: PartsMap; hiddenDamageBoost?: number }
  >;
  for (const s of symptoms) {
    const m = mods[s];
    if (!m) continue;
    if (m.addParts) merge(m.addParts);
    if (m.hiddenDamageBoost) prob += m.hiddenDamageBoost;
  }

  // Mirror the engine's phantom pruning (left/right for side-category parts,
  // front/rear for the trunk-OR-hood symptom) and implied-operations pass so
  // the preview's part count matches the eventual server estimate.
  const cat = (id: string) =>
    (ops.parts as Record<string, { category?: string }>)[id]?.category ?? "";
  const passengerSignal =
    collisionType === "side-impact-passenger" || impactPoints.includes("passenger-side");
  const driverSignal =
    collisionType === "side-impact-driver" || impactPoints.includes("driver-side");
  if (passengerSignal && !driverSignal) {
    for (const [id, sev] of [...parts]) {
      if (!id.endsWith("_left") || cat(id) !== "side") continue;
      const rightId = id.replace(/_left$/, "_right");
      if (!(rightId in ops.parts)) continue;
      parts.delete(id);
      parts.set(rightId, Math.max(parts.get(rightId) ?? 0, sev));
    }
  }
  if (symptoms.includes("trunk-hood-wont-close")) {
    const frontSignal = collisionType === "front-impact" || impactPoints.includes("front");
    const rearSignal = collisionType === "rear-impact" || impactPoints.includes("rear");
    if (frontSignal !== rearSignal) {
      for (const id of frontSignal
        ? ["rear_reinforcement_bar", "frame_rail_rear"]
        : ["front_reinforcement_bar"])
        parts.delete(id);
    }
  }
  if ([...parts.keys()].some((id) => cat(id) !== "service")) {
    merge({ diagnostic_scans: 2 });
    const adasTriggers = [
      "windshield", "front_bumper_cover", "rear_bumper_cover", "grille",
      "side_mirror_left", "side_mirror_right",
    ];
    if (adasTriggers.some((id) => parts.has(id))) merge({ adas_calibration: 3 });
    if (parts.has("ac_condenser")) merge({ ac_evac_recharge: 3 });
    if ([...parts.keys()].some((id) => ["front", "rear", "side", "struct"].includes(cat(id))))
      merge({ paint_materials: 2 });
  }

  const sevs = [...parts.values()];
  return {
    partCount: parts.size,
    maxSeverity: sevs.length ? Math.min(10, Math.max(...sevs)) : 0,
    hiddenProb: Math.min(1, Math.max(0, prob)),
  };
}
